import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import RichTextArea from "components/common/RichTextArea";
import EventService from "service/event/EventService";
import AuthenticationService from "service/auth/AuthenticationService";
import CallNoteService from "../service/CallNoteService";
import PatientProgressNotesService from "../service/PatientProgressNotesService";
import { htmlToText, sanitizeRichText, textToHtml } from "../prescription/helper/richText";
import {
  CALL_NOTE_TEMPLATE_GROUPS,
  callNoteTemplateContext,
  fillCallNoteTemplate,
} from "../helper/callNoteTemplates";
import ModalPortal from "../sections/common/ModalPortal";
import "../styles/callNote.scss";

/**
 * Call note — Figma 3108:14318, on the v2 (Mongo) endpoints.
 *
 * SYNCED FROM phr (src/modules/patient/components/CallNoteModal.js) and kept
 * identical but for two BRIDGE ADAPTATIONS:
 *   · the sanitiser comes from this module's prescription/helper/richText.js
 *     (byte-identical to phr's components/common/richText.js);
 *   · the progress note's author is the signed-in EMPLOYEE, as bridge's own
 *     progress-notes composer writes it (sections/progressNotes/ComposerModal),
 *     not phr's noteAuthor with its doctor fallback — deliberately not ported
 *     to bridge.
 * Bridge opens it from the appointments board's row menu (appointments-v2,
 * "Add callnote"), with that row's appointment — product, 2026-09-21: the
 * note belongs to the appointment flow, not the patient record, which is why
 * it was taken off bridge's phr route on 2026-09-12.
 *
 * WHAT THE BOARD IS, AND WHAT IT REMOVED. One message, one Send. No
 * conversation summary, and no "attach to call" picker — both existed only
 * because a v1 note had to hang off a hand-authored CALL PLAN row that nothing
 * linked to an appointment, so the modal had to find a call before it could
 * save anything. v2 is keyed on (patientId, appointmentId), which this screen
 * already has, so there is nothing to look up and nothing to choose. The whole
 * apparatus is gone rather than hidden.
 *
 * THE ORDERING IS STILL THE FEATURE: save -> send -> disable, stopping at the
 * first failure, because the chat body carries the note's ID rather than its
 * text. A retry after a failed push RESUMES at the push — the note already
 * exists, and re-running create would leave a second note on the appointment
 * and push only the second id.
 *
 * WHAT IS STILL NOT HERE: the conversation summary the earlier board drew. No
 * transcript source and no summariser exists anywhere in the estate, and the
 * click-to-call endpoint discards its own response, so there is not even a
 * recording id to work from. This board dropped the section; that is the honest
 * outcome either way.
 *
 * THE STATUS LINE IS CONDITIONAL, and that is the point of `variant` being
 * optional. Opened from the Call note button, this modal knows nothing about
 * how a call went — the appointment's stored status is a BOOKING state, not a
 * call outcome, and printing it as "Status - Completed" would assert something
 * nobody said. So there is no status line at all on that path.
 *
 * It appears in exactly one case: the modal is opened automatically after an
 * appointment status change SUCCEEDS, and then the status shown is the one the
 * operator just set — a fact, seconds old. A failed change opens nothing.
 *
 * THE MESSAGE STARTS EMPTY. It used to be prefilled from a canned template
 * chosen by the appointment status, which put words in front of the operator
 * that they had not written and that the status could not actually justify.
 */
const ChatIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path
      d="M2.4 2.2h9.2c.6 0 1 .4 1 1v5.4c0 .6-.4 1-1 1H5.9L3.2 11.6V9.6h-.8c-.6 0-1-.4-1-1V3.2c0-.6.4-1 1-1Z"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l8 8M14 6l-8 8" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
    <path d="M12.6 1.4L6.6 7.4M12.6 1.4L8.8 12.6l-2.2-5.2-5.2-2.2 11.2-3.8Z" />
  </svg>
);

/** Outcome word + its colour. Only the word is tinted (3108:14323). */
export const CALL_NOTE_OUTCOME = {
  completed: { label: "Completed", tone: "done" },
  cancelled: { label: "Cancelled", tone: "stop" },
  noShow: { label: "No show", tone: "warn" },
  rescheduled: { label: "Rescheduled", tone: "info" },
  confirmed: { label: "Confirmed", tone: "done" },
  /* Mid-call, and everything pre-call or mid-lab. Neither is an outcome, so
     neither gets the outcome colours. */
  inProgress: { label: "In progress", tone: "info" },
  pending: { label: "Scheduled", tone: "info" },
};

/**
 * CALL_UPDATE — progressNoteFormatters.TAGS. The same tag bridge's call-plan
 * screen writes with, so these land in the tab an operator already looks in.
 */
const CALL_UPDATE_TAG = 1;

const CallNoteModal = ({
  variant,
  statusLabel,
  patient,
  appointment,
  onClose,
}) => {
  /* No fallback: an absent variant means "no status to show", not "assume
     completed". See the header. */
  const outcome = variant ? CALL_NOTE_OUTCOME[variant] : null;
  /* The WORDS come from the status that was actually set; the variant supplies
     only the colour. Bucketing 23 statuses into five labels is what made a
     status change to "In progress" announce itself as "Confirmed" — the label
     was whatever bucket the status fell into, and unmapped ones fell into the
     green one. */
  const outcomeLabel = statusLabel || (outcome && outcome.label) || "";
  const service = useRef(new CallNoteService()).current;
  const progressNotes = useRef(new PatientProgressNotesService()).current;
  const auth = useRef(new AuthenticationService()).current;
  const mounted = useRef(true);
  const panelRef = useRef(null);
  const lastFocused = useRef(null);
  useEffect(() => () => { mounted.current = false; }, []);

  const patientId = patient && patient.id;
  const appointmentId = appointment && appointment.id;

  /* The notes already on this appointment. Not rendered as a list — the board
     has no such element — but read, because `sent` below is component state and
     dies with the modal: without this, closing and reopening offers a live Send
     for a note the patient already has. */
  const [prior, setPrior] = useState({ state: "loading", notes: [] });
  /* EMPTY. The operator writes the message; nothing is put in their mouth.
     RichTextArea is uncontrolled and writes `defaultHtml` into the DOM once on
     mount, so starting empty also removes the seeding hazard that used to make
     the box and the payload disagree. */
  const [html, setHtml] = useState("");
  /* Which template chip is on, and a counter that REMOUNTS the editor when one
     is picked. RichTextArea is uncontrolled and writes `defaultHtml` into the
     DOM exactly once on mount (see its header), so a pick can only reach the
     box by remounting it. Typing never touches this counter, so the caret is
     never disturbed mid-edit. */
  const [templateKey, setTemplateKey] = useState("");
  const [editorRev, setEditorRev] = useState(0);
  const [busy, setBusy] = useState("");
  /** Set the moment the note exists server-side, so a retry never re-creates. */
  const [noteId, setNoteId] = useState(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  /* Keyed on the two IDS rather than the appointment object: `appointment` is
     owned by the parent and rebuilt on its every render, which would re-issue
     this GET while the lazily-seeded `html` above did not re-derive — so the
     two could silently drift apart. */
  useEffect(() => {
    if (!patientId || !appointmentId) {
      setPrior({ state: "ready", notes: [] });
      return;
    }
    service.listForAppointment(
      patientId,
      appointmentId,
      (notes) => mounted.current && setPrior({ state: "ready", notes }),
      /* A failed read is NOT a blocker: the note can still be written. It only
         costs the already-sent notice, so it degrades to "unknown" rather than
         disabling the screen. */
      () => mounted.current && setPrior({ state: "error", notes: [] })
    );
  }, [patientId, appointmentId, service]);

  /* A note that reached the chat is disabled server-side. */
  const alreadySent = prior.notes.some((n) => n && n.disabled);

  const hasText = htmlToText(html).trim().length > 0;

  /* Why Send is disabled. `emptyMessage` is deliberately NOT in here: an empty
     field on open is not a mistake to scold the operator for, the board has no
     such line, and the disabled button carries the same reason in its title. */
  const blockedReason = !appointmentId
    ? "This note needs an appointment to hang off, and none was passed."
    : "";
  const emptyMessage = !hasText;

  /* A chip REPLACES whatever is in the box, and the operator is free to edit
     after picking or to pick another. Nothing fills itself: a84bc8d removed the
     automatic prefill because a status change put words in the operator's
     mouth — a chip they click is the operator asking for those words. */
  const pickTemplate = useCallback(
    (tpl) => {
      if (!tpl || !tpl.message) return;
      /* The copy is parameterised (patient, provider, the call's time and
         date). Anything this screen cannot resolve stays as product's own
         placeholder — see callNoteTemplateContext — so a gap reads as a gap
         the operator must fill, never as a sentence with a hole in it. */
      const filled = fillCallNoteTemplate(
        tpl.message,
        callNoteTemplateContext({ patient, appointment })
      );
      setTemplateKey(tpl.key);
      /* textToHtml, NOT sanitizeRichText: `filled` is plain TEXT, and the
         sanitiser is an HTML allow-list — it reads product's own "<user name>"
         as an unknown element and unwraps it away, which turned an unresolved
         name into "Dear ," (verified under jsdom). Escaping is the
         documented boundary for seeding this editor; the wire is still
         sanitised in submit(). */
      setHtml(textToHtml(filled));
      setEditorRev((n) => n + 1);
    },
    [patient, appointment]
  );

  /**
   * Push a SAVED note to the chat, then lock it.
   *
   * Split out of `submit` so a failed push can be retried on its own: the note
   * is already saved and the work is not lost, only unsent.
   */
  const push = useCallback(
    (id) => {
      setBusy("sending");
      service.sendToChat(
        patientId,
        id,
        () => {
          if (!mounted.current) return;
          // Bridge locks the moment the chat accepts it. A failure here is not
          // an error — the patient HAS the note — but it is surfaced, because
          // it means nothing server-side now stops a second push.
          /* A TOAST, not the in-modal warning it used to be: the modal closes
             itself on a successful send now (see below), so a line painted
             inside it would never be read — and this one matters, because it
             means nothing server-side stops a second push. */
          service.disable(
            id,
            () => {},
            () =>
              EventService.emit("showError", {
                message: "Sent, but the note couldn’t be locked — don’t send it again.",
              })
          );
          setBusy("");
          setSent(true);
          /* ToastHost listens for "showError" ONLY and takes the flavour from
             `tone` (ToastHost.js:27,39). The old emit was "showSuccess", which
             has no listener anywhere — the confirmation was dropped on the
             floor. Renaming alone is not enough: with no `tone` it defaults to
             "error" and paints this red. */
          EventService.emit("showError", {
            message: "Call note sent to the patient’s chat.",
            tone: "success",
          });
          /* SENT MEANS DONE (Nittin, 2026-09-12). The modal used to stay open
             behind a disabled "Sent" button and a green line, which reads as
             "something here is still pending" on a form that can no longer be
             used for anything. The toast is the confirmation and it outlives
             the modal. Nothing below this depends on the component staying
             mounted — `setSent` above is for the case where a parent keeps it
             mounted and merely hides it. */
          onClose();
        },
        (err) => {
          if (!mounted.current) return;
          setBusy("");
          setError(
            `Saved, but couldn’t send to chat: ${(err && err.message) || "unknown error"}. ` +
              "Press Send again to retry — it won’t create a second note."
          );
        }
      );
    },
    [patientId, service, onClose]
  );

  /** Save, then push. A retry after a failed push resumes at the push. */
  const submit = useCallback(() => {
    if (blockedReason || busy || sent) return;
    setError("");
    setWarning("");
    if (noteId) {
      push(noteId);
      return;
    }
    setBusy("saving");
    /* RichTextArea emits raw contentEditable innerHTML and explicitly leaves
       sanitising to its caller; the server stores content verbatim and the
       patient app renders it, so something has to clean it here.

       An ALLOW-LIST, not a flatten. Flattening to escaped text was safe but
       also threw away the bold and the bullets the operator had just applied,
       which made every button on the toolbar decorative — the formatting never
       survived the send. This keeps the structure a person applied and drops
       every attribute, style and unknown tag that a paste from Word brings
       with it. */
    const content = sanitizeRichText(html, { headings: true, fontSize: true });
    const profile = auth.getUserProfile() || {};

    /* phr logs in as an EMPLOYEE, so employeeProfile is the populated one —
       doctorProfile can be a placeholder with id 0. */
    const writeProgressNote = (body) => {
      /* BRIDGE: the employee, exactly as this module's progress-notes composer
         writes its author (ComposerModal.js postNote). */
      const employee = profile.employeeProfile || {};
      progressNotes.createProgressNote(
        {
          patientId: patientId,
          body,
          author: {
            name: `${employee.firstName || ""} ${employee.lastName || ""}`.trim(),
            employeeId: employee.id,
          },
          tag: CALL_UPDATE_TAG,
          acknowledgers: [],
          comments: [],
        },
        () => {},
        (err) =>
          // eslint-disable-next-line no-console
          console.error("call note saved, but its progress note did not:", err)
      );
    };
    service.create(
      {
        patientId,
        appointmentId,
        /* 0 for an ops employee, who has no doctorProfile. The server accepts
           that and omits the doctor block on the read rather than refusing the
           write — refusing would lock this feature out of the employee flow
           phr signs in through. */
        doctorId: (profile.doctorProfile && profile.doctorProfile.id) || 0,
        contentHtml: content,
      },
      (note) => {
        /* A 24-char ObjectID hex string now, not v1's integer. */
        const id = note && note.id;
        if (!mounted.current) return;
        if (!id) {
          setBusy("");
          setError("The note saved but the server didn’t return its id, so it can’t be sent.");
          return;
        }
        setNoteId(id);
        /* A call note is a thing that happened to this patient, so it also goes
           into their progress notes under CALL_UPDATE — the tag bridge's own
           call-plan screen has always used for this.

           FIRE AND FORGET, and deliberately so: the call note is the primary
           action and it has already been saved by the time we get here. Failing
           the whole flow because a secondary record did not write would lose
           work the operator can see succeeded. A failure is logged, not shown.

           Placed on the CREATE success rather than after the chat push, so a
           retry of a failed push cannot write a second progress note — the
           retry resumes at the push and never re-enters this branch. */
        writeProgressNote(content);
        push(id);
      },
      (err) => {
        if (!mounted.current) return;
        setBusy("");
        setError(`Couldn’t save the note: ${(err && err.message) || "unknown error"}`);
      }
    );
  }, [blockedReason, busy, sent, noteId, patientId, appointmentId, html, push, auth, service, progressNotes]);

  /* Escape, scroll lock and focus — the same block RescheduleModal runs, and
     suspended the same way while a send is in flight. */
  useEffect(() => {
    lastFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (panelRef.current) panelRef.current.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (lastFocused.current && lastFocused.current.focus) lastFocused.current.focus();
    };
  }, []);

  const busyRef = useRef("");
  busyRef.current = busy;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busyRef.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const actionsDisabled = !!blockedReason || emptyMessage || !!busy || sent;

  return (
    /* Portalled: `.ph-sticky` is `position: sticky` with a z-index, and sticky
       always creates a stacking context — so a fixed backdrop rendered inside
       the header is trapped at that layer whatever z-index it asks for. Every
       other overlay on this screen already goes through here. */
    <ModalPortal>
    <div
      className="cn-backdrop"
      role="presentation"
      /* mousedown, not click, and only when the backdrop is itself the target:
         a `click` fires on the nearest common ancestor of down and up, so
         drag-selecting the message and releasing outside the sheet used to
         close the modal and throw the draft away. Suspended while a send is in
         flight, when closing would hide a push that is still going out. */
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="cn-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Call note"
        ref={panelRef}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="cn-head">
          <div className="cn-head-text">
            <h2 className="cn-title">Call note</h2>
            {/* Only when a status change just succeeded — see the header. The
                board draws "Status - Completed" (3108:14323) with the outcome
                word tinted; opened from the button there is no status to draw,
                so the line is absent rather than guessed. */}
            {outcome && outcomeLabel ? (
              <p className="cn-sub">
                Status -{" "}
                <span className={`cn-outcome is-${outcome.tone}`}>{outcomeLabel}</span>
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="cn-close"
            aria-label="Close"
            disabled={!!busy}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="cn-rule" />

        <div className="cn-body">
          {/* Templates (6091:3671). Two groups in the board's order; a chip
              writes its own copy into the message below. */}
          <section className="cn-block cn-templates">
            <h3 className="cn-block-title">Choose a template</h3>
            {CALL_NOTE_TEMPLATE_GROUPS.map((group) => (
              <div className="cn-tpl-group" key={group.key}>
                <p className="cn-tpl-group-label">{group.label}</p>
                <div className="cn-tpl-chips">
                  {group.templates.map((tpl) => {
                    const on = templateKey === tpl.key;
                    /* No copy yet means the chip cannot be picked: it would
                       blank the box and offer an empty note for sending. */
                    const noCopy = !tpl.message;
                    return (
                      <button
                        type="button"
                        key={tpl.key}
                        className={`cn-chip${on ? " is-on" : ""}`}
                        aria-pressed={on}
                        disabled={noCopy || !!busy}
                        title={noCopy ? "This template has no message yet" : undefined}
                        onClick={() => pickTemplate(tpl)}
                      >
                        {tpl.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          <section className="cn-block">
            <div className="cn-block-head is-spread">
              <h3 className="cn-block-title">Message to patient</h3>
            </div>
            <p className="cn-hint">
              <span className="cn-hint-icon">
                <ChatIcon />
              </span>
              This message will be sent to the patient in their chat.
            </p>

            <RichTextArea
              key={editorRev}
              className="cn-editor"
              /* The board's toolbar has a block-style and a size menu
                 (6089:3673). Paired with the sanitiser options in submit(): a
                 menu whose markup the save would drop must not be offered. */
              styleControls
              defaultHtml={html}
              onChange={setHtml}
              placeholder="Pick a template above, or write your own message…"
              ariaLabel="Message to patient"
            />
          </section>

          {/* A note already reached this patient for this appointment. Not a
              block — a follow-up note is legitimate and the server allows
              several — but the operator should know before sending the same
              prefilled text twice. */}
          {alreadySent && !sent && (
            <p className="cn-note-blocked">
              A note has already been sent to this patient for this appointment.
            </p>
          )}
          {blockedReason && !sent && <p className="cn-note-blocked">{blockedReason}</p>}
          {error && <p className="cn-error">{error}</p>}
          {warning && <p className="cn-warn">{warning}</p>}
          {sent && <p className="cn-sent">Sent to the patient&rsquo;s chat.</p>}
        </div>

        <footer className="cn-foot">
          <button
            type="button"
            className="cn-send"
            disabled={actionsDisabled}
            title={
              sent
                ? "Already sent — a note can only be pushed once"
                : blockedReason || (emptyMessage ? "Write a message first" : undefined)
            }
            onClick={submit}
          >
            <SendIcon />
            {busy === "saving"
              ? "Saving…"
              : busy === "sending"
              ? "Sending…"
              : sent
              ? "Sent"
              : noteId
              ? "Retry send"
              : "Send to chat"}
          </button>
        </footer>
      </div>
    </div>
    </ModalPortal>
  );
};

CallNoteModal.propTypes = {
  /** The words for the status line — the label of the status actually set.
      Falls back to the variant's own label when a caller has none. */
  statusLabel: PropTypes.string,
  /**
   * One of CALL_NOTE_OUTCOME's keys, or absent. Decides the COLOUR only.
   *
   * Present ONLY when this modal was opened automatically after a successful
   * appointment status change, and it names the status that was just set.
   * Absent when the operator opened it from the Call note button, where no
   * outcome is known — the header then has no status line at all.
   */
  variant: PropTypes.string,
  /** Needed for the chat push and the call-plan lookup. */
  patient: PropTypes.object,
  /** Needed for the template and the call-plan date window. */
  appointment: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default CallNoteModal;
